import { PoseData, GestureEvent, IGestureRecognizer, GestureType, PersonID } from '../../types';
import { GestureDebouncer } from './GestureDebouncer';
import { ArmsRaisedDetector } from './detectors/ArmsRaisedDetector';
import { ArmsCrossedDetector } from './detectors/ArmsCrossedDetector';
import { ArmExtendedDetector } from './detectors/ArmExtendedDetector';
import { ArmRaisedDetector } from './detectors/ArmRaisedDetector';
import { logger } from '../../utils/logger';

/**
 * GestureRecognizer analyzes pose data to detect gestures
 * Validates Requirements 3.3.4, 3.4.3, 4.1, 4.4
 * Supports both single-person and multi-person modes with per-person gesture state
 */
export class GestureRecognizer implements IGestureRecognizer {
  // Single-person state (for backward compatibility)
  private debouncer: GestureDebouncer;
  
  // Multi-person state (Map<PersonID, GestureState>)
  private gestureStates: Map<PersonID, { debouncer: GestureDebouncer; activeGestures: Set<GestureType> }> = new Map();
  
  private armsRaisedDetector: ArmsRaisedDetector;
  private armsCrossedDetector: ArmsCrossedDetector;
  private armExtendedDetector: ArmExtendedDetector;
  private armRaisedDetector: ArmRaisedDetector;
  
  // Performance tracking
  private gestureDetectionTimes: number[] = [];
  private readonly maxSamples = 60;
  private performanceWarningLogged = false;
  
  constructor() {
    this.debouncer = new GestureDebouncer();
    this.armsRaisedDetector = new ArmsRaisedDetector();
    this.armsCrossedDetector = new ArmsCrossedDetector();
    this.armExtendedDetector = new ArmExtendedDetector();
    this.armRaisedDetector = new ArmRaisedDetector();
  }
  
  /**
   * Get or create gesture state for a specific person
   * Validates Requirements 4.1, 4.4
   * @param personId Person identifier
   * @returns Gesture state for the person
   */
  private getOrCreateState(personId: PersonID): { debouncer: GestureDebouncer; activeGestures: Set<GestureType> } {
    if (!this.gestureStates.has(personId)) {
      this.gestureStates.set(personId, {
        debouncer: new GestureDebouncer(),
        activeGestures: new Set()
      });
    }
    return this.gestureStates.get(personId)!;
  }
  
  /**
   * Get average gesture detection time
   */
  getAverageDetectionTime(): number {
    if (this.gestureDetectionTimes.length === 0) {
      return 0;
    }
    const sum = this.gestureDetectionTimes.reduce((a, b) => a + b, 0);
    return sum / this.gestureDetectionTimes.length;
  }
  
  /**
   * Get gesture detection performance stats
   */
  getPerformanceStats(): { average: number; min: number; max: number; samples: number } {
    if (this.gestureDetectionTimes.length === 0) {
      return { average: 0, min: 0, max: 0, samples: 0 };
    }
    
    const sum = this.gestureDetectionTimes.reduce((a, b) => a + b, 0);
    return {
      average: sum / this.gestureDetectionTimes.length,
      min: Math.min(...this.gestureDetectionTimes),
      max: Math.max(...this.gestureDetectionTimes),
      samples: this.gestureDetectionTimes.length
    };
  }
  
  /**
   * Log gesture detection performance
   */
  logPerformance(): void {
    const stats = this.getPerformanceStats();
    logger.group('🎯 Gesture Detection Performance', () => {
      logger.log(`Average: ${stats.average.toFixed(2)}ms`);
      logger.log(`Min: ${stats.min.toFixed(2)}ms`);
      logger.log(`Max: ${stats.max.toFixed(2)}ms`);
      logger.log(`Samples: ${stats.samples}`);
      logger.log(`Meets <5ms requirement: ${stats.average < 5 ? '✓ YES' : '✗ NO'}`);
    });
  }
  
  /**
   * Track gesture detection performance
   */
  private trackPerformance(detectionTime: number): void {
    this.gestureDetectionTimes.push(detectionTime);
    
    // Keep only recent samples
    if (this.gestureDetectionTimes.length > this.maxSamples) {
      this.gestureDetectionTimes.shift();
    }
    
    // Log warning if detection takes too long (>5ms target)
    if (detectionTime > 5 && !this.performanceWarningLogged) {
      console.warn(`⚠️ Gesture detection slow: ${detectionTime.toFixed(2)}ms (target: <5ms)`);
      this.performanceWarningLogged = true;
      
      // Reset warning flag after 5 seconds
      setTimeout(() => {
        this.performanceWarningLogged = false;
      }, 5000);
    }
  }
  
  /**
   * Detect all currently active gestures without debouncing
   * Used for continuous display of gesture names
   * Validates Requirements 3.3.9, 3.2.13, 4.1, 4.4
   * @param pose Current pose data
   * @param personId Optional person identifier for multi-person mode
   * @returns Array of all currently detected gesture types (no debouncing)
   */
  detectActiveGestures(pose: PoseData, personId?: PersonID): GestureType[] {
    const startTime = performance.now();
    
    const activeGestures: GestureType[] = [];
    const timestamp = pose.timestamp;
    
    // Run all gesture detectors
    const armsRaised = this.armsRaisedDetector.detect(pose, timestamp);
    const armsCrossed = this.armsCrossedDetector.detect(pose, timestamp);
    const leftArmExtended = this.armExtendedDetector.detectLeft(pose, timestamp);
    const rightArmExtended = this.armExtendedDetector.detectRight(pose, timestamp);
    const leftArmRaised = this.armRaisedDetector.detectLeft(pose, timestamp);
    const rightArmRaised = this.armRaisedDetector.detectRight(pose, timestamp);
    
    // Apply gesture priority rules (Validates Requirements 3.3.5)
    // Priority Rule: If ARMS_RAISED (both arms) is detected, suppress single arm raised gestures
    const filteredLeftArmRaised = armsRaised ? null : leftArmRaised;
    const filteredRightArmRaised = armsRaised ? null : rightArmRaised;
    
    // Collect all detected gestures (no debouncing)
    if (armsRaised) activeGestures.push(armsRaised.type);
    if (armsCrossed) activeGestures.push(armsCrossed.type);
    if (leftArmExtended) activeGestures.push(leftArmExtended.type);
    if (rightArmExtended) activeGestures.push(rightArmExtended.type);
    if (filteredLeftArmRaised) activeGestures.push(filteredLeftArmRaised.type);
    if (filteredRightArmRaised) activeGestures.push(filteredRightArmRaised.type);
    
    // Store active gestures in per-person state if personId provided
    if (personId !== undefined) {
      const state = this.getOrCreateState(personId);
      state.activeGestures = new Set(activeGestures);
    }
    
    const endTime = performance.now();
    this.trackPerformance(endTime - startTime);
    
    return activeGestures;
  }
  
  /**
   * Analyze pose data for gestures
   * Used for debounced action triggering (color change, mode toggle, etc.)
   * Validates Requirements 4.1, 4.4
   * @param pose Current pose data
   * @param personId Optional person identifier for multi-person mode
   * @returns Array of detected gesture events (after debouncing and priority filtering)
   */
  analyzePose(pose: PoseData, personId?: PersonID): GestureEvent[] {
    const startTime = performance.now();
    
    const detectedGestures: GestureEvent[] = [];
    const timestamp = pose.timestamp;
    
    // Get the appropriate debouncer (per-person or single-person)
    const debouncer = personId !== undefined 
      ? this.getOrCreateState(personId).debouncer 
      : this.debouncer;
    
    // Run all gesture detectors
    const armsRaised = this.armsRaisedDetector.detect(pose, timestamp);
    const armsCrossed = this.armsCrossedDetector.detect(pose, timestamp);
    const leftArmExtended = this.armExtendedDetector.detectLeft(pose, timestamp);
    const rightArmExtended = this.armExtendedDetector.detectRight(pose, timestamp);
    const leftArmRaised = this.armRaisedDetector.detectLeft(pose, timestamp);
    const rightArmRaised = this.armRaisedDetector.detectRight(pose, timestamp);
    
    // Apply gesture priority rules (Validates Requirements 3.3.5, 3.3.6)
    // Priority Rule: If ARMS_RAISED (both arms) is detected, suppress single arm raised gestures
    // This ensures clean transitions: single arm → both arms → single arm
    // Other gestures (ARMS_CROSSED, arm extended) are independent and not affected by priority
    const filteredLeftArmRaised = armsRaised ? null : leftArmRaised;
    const filteredRightArmRaised = armsRaised ? null : rightArmRaised;
    
    // Apply debouncing to each detected gesture
    if (armsRaised && debouncer.shouldTrigger(armsRaised.type, true, timestamp)) {
      detectedGestures.push(armsRaised);
    } else if (!armsRaised) {
      debouncer.shouldTrigger(GestureType.ARMS_RAISED, false, timestamp);
    }
    
    if (armsCrossed && debouncer.shouldTrigger(armsCrossed.type, true, timestamp)) {
      detectedGestures.push(armsCrossed);
    } else if (!armsCrossed) {
      debouncer.shouldTrigger(GestureType.ARMS_CROSSED, false, timestamp);
    }
    
    if (leftArmExtended && debouncer.shouldTrigger(leftArmExtended.type, true, timestamp)) {
      detectedGestures.push(leftArmExtended);
    } else if (!leftArmExtended) {
      debouncer.shouldTrigger(GestureType.LEFT_ARM_EXTENDED, false, timestamp);
    }
    
    if (rightArmExtended && debouncer.shouldTrigger(rightArmExtended.type, true, timestamp)) {
      detectedGestures.push(rightArmExtended);
    } else if (!rightArmExtended) {
      debouncer.shouldTrigger(GestureType.RIGHT_ARM_EXTENDED, false, timestamp);
    }
    
    if (filteredLeftArmRaised && debouncer.shouldTrigger(filteredLeftArmRaised.type, true, timestamp)) {
      detectedGestures.push(filteredLeftArmRaised);
    } else if (!filteredLeftArmRaised) {
      debouncer.shouldTrigger(GestureType.LEFT_ARM_RAISED, false, timestamp);
    }
    
    if (filteredRightArmRaised && debouncer.shouldTrigger(filteredRightArmRaised.type, true, timestamp)) {
      detectedGestures.push(filteredRightArmRaised);
    } else if (!filteredRightArmRaised) {
      debouncer.shouldTrigger(GestureType.RIGHT_ARM_RAISED, false, timestamp);
    }
    
    const endTime = performance.now();
    this.trackPerformance(endTime - startTime);
    
    return detectedGestures;
  }
  
  /**
   * Reset gesture state for a specific person
   * Called when a person exits the frame in multi-person mode
   * Validates Requirements 4.1
   * @param personId Person identifier to reset
   */
  resetPerson(personId: PersonID): void {
    this.gestureStates.delete(personId);
  }
  
  /**
   * Reset all gesture debounce states
   */
  reset(): void {
    this.debouncer.resetAll();
    this.gestureStates.clear();
  }
}
